import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ApiHttpError,
  type ApiConfig,
  listIntegrationCatalog,
  listIntegrations,
  submitBqGa4Integration,
  submitKlaviyoIntegration,
  submitKlaviyoOutboundIntegration,
  submitMagentoIntegration,
} from "../api";
import {
  ROTATION_CYCLE_OPTIONS,
  formatNotificationState,
  formatRelativeTime,
  formatRotationCycle,
  notificationSeverityClass,
} from "../format";
import {
  integrationCheckCompleted,
  type PersistedIntegrationCheck,
} from "../integrationCheck";
import type {
  BqGa4CredentialsBlock,
  ExpiryPolicyRequest,
  ExpiryState,
  IntegrationCatalogItem,
  IntegrationState,
  KlaviyoCredentialsBlock,
  KlaviyoOutboundCredentialsBlock,
  MagentoCredentialsBlock,
  RotationCycle,
  ScopeRequirement,
  ScopeStatus,
  SourceSystem,
} from "../types";

const CHECK_TTL_MS = 15 * 60 * 1000;
const MAX_NOTIFICATION_EMAILS = 10;

const EMPTY_MAGENTO: MagentoCredentialsBlock = {
  base_url: "",
  store_code: "default",
  website_id: "",
  oauth_consumer_key: "",
  oauth_consumer_secret: "",
  oauth_access_token: "",
  oauth_access_token_secret: "",
};

const EMPTY_BQ_GA4: BqGa4CredentialsBlock = {
  project_id: "",
  dataset_id: "",
  service_account_json: "",
};

const EMPTY_KLAVIYO: KlaviyoCredentialsBlock = {
  api_key: "",
  api_revision: "",
};

const EMPTY_KLAVIYO_OUTBOUND: KlaviyoOutboundCredentialsBlock = {
  api_key: "",
  api_revision: "",
  customer_id_property: "",
};

interface ExpiryFormState {
  trackingEnabled: boolean;
  expiresAtUtc: string;
  rotationCycle: RotationCycle;
  rotationCycleDays: string;
  notificationEmails: string[];
}

const EMPTY_EXPIRY: ExpiryFormState = {
  trackingEnabled: false,
  expiresAtUtc: "",
  rotationCycle: "quarterly",
  rotationCycleDays: "",
  notificationEmails: [],
};

export function IntegrationsPanel({ config }: { config: ApiConfig }) {
  const [catalog, setCatalog] = useState<IntegrationCatalogItem[]>([]);
  const [states, setStates] = useState<IntegrationState[]>([]);
  const [selected, setSelected] = useState<SourceSystem>(
    () => readPersistedCheck(config)?.source ?? "magento",
  );
  const [magento, setMagento] = useState<MagentoCredentialsBlock>(EMPTY_MAGENTO);
  const [bqGa4, setBqGa4] = useState<BqGa4CredentialsBlock>(EMPTY_BQ_GA4);
  const [klaviyo, setKlaviyo] = useState<KlaviyoCredentialsBlock>(EMPTY_KLAVIYO);
  const [klaviyoOutbound, setKlaviyoOutbound] =
    useState<KlaviyoOutboundCredentialsBlock>(EMPTY_KLAVIYO_OUTBOUND);
  const [editCredentials, setEditCredentials] = useState<boolean>(false);
  const [expiry, setExpiry] = useState<ExpiryFormState>(EMPTY_EXPIRY);
  const [checkingSource, setCheckingSource] = useState<SourceSystem | null>(() =>
    readPersistedCheck(config)?.source ?? null,
  );
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<UiError | null>(null);
  // Per-scope verdict from the most recent FAILED test (error.details.scopes),
  // used to colour the Required-access rows red. Cleared on a new submit / switch.
  const [failedScopes, setFailedScopes] = useState<Record<string, ScopeStatus> | null>(null);
  // True while onSubmit owns the result. The poll-based "configured" detector
  // below must defer to onSubmit during an in-page submit, otherwise it fires a
  // premature "configured successfully" for an already-configured source before
  // the live validation result comes back.
  const submitInFlight = useRef(false);

  const refresh = useCallback(async () => {
    try {
      const [catalogResp, statesResp] = await Promise.all([
        listIntegrationCatalog(config),
        listIntegrations(config),
      ]);
      setCatalog(catalogResp.data);
      setStates(statesResp.data);
      setError(null);
    } catch (e) {
      setError(formatError(e));
    }
  }, [config]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    if (!checkingSource) return;
    const id = window.setInterval(() => {
      void refresh();
    }, 4000);
    return () => window.clearInterval(id);
  }, [checkingSource, refresh]);

  useEffect(() => {
    if (!checkingSource) return;
    // onSubmit owns the result of an in-page submit; only the reload-resume
    // path (no active submit) should resolve "checking" from polled state.
    if (submitInFlight.current) return;
    const persistedCheck = readPersistedCheck(config);
    const refreshedState = states.find(
      (state) => state.source_system === checkingSource,
    );
    if (
      persistedCheck
      && refreshedState
      && integrationCheckCompleted(persistedCheck, refreshedState)
      && isConfigured(refreshedState.status)
    ) {
      clearPersistedCheck(config);
      setCheckingSource(null);
      setNotice(submissionNotice(checkingSource, refreshedState.status));
    }
  }, [checkingSource, config, states]);

  const selectedCatalog = useMemo(
    () => catalog.find((item) => item.source_system === selected),
    [catalog, selected],
  );
  const selectedState = useMemo(
    () => states.find((item) => item.source_system === selected),
    [states, selected],
  );
  const selectedConfigured = isConfigured(selectedState?.status);

  // Hydrate the expiry form when a new source is selected. We only sync FROM
  // the server when the user is not actively editing - otherwise typing into
  // a field could be wiped by an async refresh.
  useEffect(() => {
    if (selectedState?.expiry) {
      setExpiry(toFormState(selectedState.expiry));
    } else {
      setExpiry(EMPTY_EXPIRY);
    }
    // Reset creds-edit flag when switching sources or status transitions.
    setEditCredentials(!selectedConfigured);
    setMagento(EMPTY_MAGENTO);
    setBqGa4(EMPTY_BQ_GA4);
    setKlaviyo(EMPTY_KLAVIYO);
    setKlaviyoOutbound(EMPTY_KLAVIYO_OUTBOUND);
    setError(null);
    setNotice(null);
    setFailedScopes(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, selectedState?.source_system, selectedState?.status]);

  const cooldown = computeCooldown(
    selectedState?.credentials_updated_at_utc,
    selectedState?.submitted_at_utc,
    error,
  );
  const credentialsDisabled = !editCredentials || checkingSource !== null || cooldown.active;
  const expiryDisabled = checkingSource !== null;
  const submitDisabled =
    (!editCredentials && !expiryDirtyOrChanged(expiry, selectedState?.expiry))
    || (editCredentials && cooldown.active)
    || checkingSource !== null;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (submitDisabled) return;
    setNotice(null);
    setError(null);
    setFailedScopes(null);

    // Build expiry_policy block - always sent when the section was touched
    // OR when tracking is currently enabled (so toggling off is captured).
    const existingExpiry = selectedState?.expiry;
    const useActivationCadence =
      editCredentials
      && expiry.trackingEnabled
      && existingExpiry?.tracking_enabled === true
      && expiry.expiresAtUtc === (existingExpiry.expires_at_utc ?? "");
    const expiryPolicy = buildExpiryPolicyRequest(
      expiry,
      useActivationCadence ? "cadence_derived" : "explicit",
    );
    if (expiry.trackingEnabled) {
      const validationError = validateExpiryPolicy(expiryPolicy);
      if (validationError) {
        setError({ title: "Check expiry settings", message: validationError });
        return;
      }
    }

    const body: { credentials?: unknown; expiry_policy?: ExpiryPolicyRequest } = {
      expiry_policy: expiryPolicy,
    };
    if (editCredentials) {
      body.credentials =
        selected === "magento"
          ? magento
          : selected === "klaviyo"
            ? klaviyo
            : selected === "klaviyo_outbound"
              ? klaviyoOutbound
              : bqGa4;
      submitInFlight.current = true;
      persistChecking(
        config,
        selected,
        selectedState?.credential_generation_id ?? selectedState?.request_id ?? null,
      );
      setCheckingSource(selected);
    }

    try {
      let response: { data: IntegrationState };
      if (selected === "magento") {
        response = await submitMagentoIntegration(config, body as never);
      } else if (selected === "klaviyo") {
        response = await submitKlaviyoIntegration(config, body as never);
      } else if (selected === "klaviyo_outbound") {
        response = await submitKlaviyoOutboundIntegration(config, body as never);
      } else {
        response = await submitBqGa4Integration(config, body as never);
      }
      clearPersistedCheck(config);
      if (editCredentials) {
        setNotice(submissionNotice(selected, response.data.status));
        setMagento(EMPTY_MAGENTO);
        setBqGa4(EMPTY_BQ_GA4);
        setKlaviyo(EMPTY_KLAVIYO);
        setKlaviyoOutbound(EMPTY_KLAVIYO_OUTBOUND);
        setEditCredentials(false);
      } else {
        setNotice("Expiry policy saved.");
      }
      // Keep the per-row "Checking..." state until the fresh verdict is loaded,
      // so the scope rows flip straight from Checking to Granted/Denied.
      await refresh();
      setCheckingSource(null);
    } catch (err) {
      clearPersistedCheck(config);
      setCheckingSource(null);
      setError(formatError(err));
      if (err instanceof ApiHttpError) {
        const map = scopeStatusMap(err.details?.scopes);
        if (Object.keys(map).length > 0) setFailedScopes(map);
      }
    } finally {
      submitInFlight.current = false;
    }
  }

  return (
    <main className="integrations">
      <section className="integration-list" aria-label="Integration status">
        <div className="section-title">Integrations</div>
        {states.map((state) => (
          <button
            key={state.source_system}
            className={"integration-row " + (selected === state.source_system ? "selected" : "")}
            onClick={() => setSelected(state.source_system)}
          >
            <span className="integration-row-main">
              <strong>{state.display_name}</strong>
              <small>{state.category}</small>
              <small className="last-sync">
                Last updated: {formatRelativeTime(lastCustomerActivity(state))}
              </small>
            </span>
            <span className="integration-row-pills">
              <span className={"status-pill status-" + state.status}>
                {labelStatus(state.status)}
              </span>
              {state.expiry?.tracking_enabled && state.expiry?.notification_state
                && state.expiry.notification_state !== "active" && (
                <span
                  className={"status-pill expiry-pill " + notificationSeverityClass(state.expiry.notification_state)}
                  title={formatNotificationState(state.expiry.notification_state, state.expiry.days_remaining) ?? ""}
                >
                  {pillTextForState(state.expiry.notification_state, state.expiry.days_remaining)}
                </span>
              )}
            </span>
          </button>
        ))}
      </section>

      <section className="integration-form-wrap">
        <div className="integration-form-head">
          <div>
            <h2>{selectedCatalog?.display_name ?? selected}</h2>
            <p>{selectedCatalog?.category}</p>
          </div>
          {selectedState && (
            <span className={"status-pill status-" + selectedState.status}>
              {labelStatus(selectedState.status)}
            </span>
          )}
        </div>

        {error && (
          <div className="error-banner inline" role="alert">
            <strong>{error.title}</strong>
            <span>{error.message}</span>
          </div>
        )}
        {notice && <div className="notice-banner">{notice}</div>}
        {checkingSource && (
          <div className="notice-banner">
            Checking {sourceDisplayName(checkingSource)} connection...
          </div>
        )}
        {cooldown.active && (
          <div className="cooldown-banner" role="status">
            Credentials can be changed at most once per 24 hours. You can change them again in{" "}
            <strong>{cooldown.label}</strong>. For an earlier change, contact your Growdhi
            customer success representative.
          </div>
        )}
        {selectedState?.expiry?.tracking_enabled
          && selectedState.expiry.notification_state
          && selectedState.expiry.notification_state !== "active" && (
          <div
            className={"expiry-banner " + notificationSeverityClass(selectedState.expiry.notification_state)}
            role="status"
          >
            {formatNotificationState(selectedState.expiry.notification_state, selectedState.expiry.days_remaining)}
            {selectedState.expiry.email_notification && (
              <span className="expiry-banner-meta">
                {" "}A reminder is queued for{" "}
                {selectedState.expiry.email_notification.recipients.join(", ")}.
              </span>
            )}
          </div>
        )}

        <form className="integration-form" onSubmit={onSubmit}>
          <CredentialsSection
            selected={selected}
            configured={selectedConfigured}
            editing={editCredentials}
            disabled={credentialsDisabled}
            magento={magento}
            bqGa4={bqGa4}
            klaviyo={klaviyo}
            klaviyoOutbound={klaviyoOutbound}
            persistedMagentoMetadata={selectedState?.metadata}
            persistedBqGa4Metadata={selectedState?.metadata}
            persistedKlaviyoMetadata={selectedState?.metadata}
            persistedKlaviyoOutboundMetadata={selectedState?.metadata}
            onToggleEdit={(next) => {
              setEditCredentials(next);
              if (!next) {
                setMagento(EMPTY_MAGENTO);
                setBqGa4(EMPTY_BQ_GA4);
                setKlaviyo(EMPTY_KLAVIYO);
                setKlaviyoOutbound(EMPTY_KLAVIYO_OUTBOUND);
              }
            }}
            onChangeMagento={setMagento}
            onChangeBqGa4={setBqGa4}
            onChangeKlaviyo={setKlaviyo}
            onChangeKlaviyoOutbound={setKlaviyoOutbound}
          />

          <ScopeRequirementsSection
            requirements={selectedCatalog?.scope_requirements ?? []}
            statuses={failedScopes ?? extractScopeStatuses(selectedState?.validation_summary)}
            checking={checkingSource === selected}
          />

          <ExpirySection
            disabled={expiryDisabled}
            expiry={expiry}
            activationPending={selectedState?.status === "pending_operator_setup"}
            persistedRecipients={selectedState?.expiry?.notification_emails ?? []}
            onChange={setExpiry}
          />

          <button className="primary submit-intake" disabled={submitDisabled}>
            {submitButtonLabel({
              editCredentials,
              checkingSource,
              cooldownActive: cooldown.active,
            })}
          </button>
        </form>
      </section>
    </main>
  );
}

// ---------------------------------------------------------------------------
// Credentials section
// ---------------------------------------------------------------------------

function CredentialsSection({
  selected,
  configured,
  editing,
  disabled,
  magento,
  bqGa4,
  klaviyo,
  klaviyoOutbound,
  persistedMagentoMetadata,
  persistedBqGa4Metadata,
  persistedKlaviyoMetadata,
  persistedKlaviyoOutboundMetadata,
  onToggleEdit,
  onChangeMagento,
  onChangeBqGa4,
  onChangeKlaviyo,
  onChangeKlaviyoOutbound,
}: {
  selected: SourceSystem;
  configured: boolean;
  editing: boolean;
  disabled: boolean;
  magento: MagentoCredentialsBlock;
  bqGa4: BqGa4CredentialsBlock;
  klaviyo: KlaviyoCredentialsBlock;
  klaviyoOutbound: KlaviyoOutboundCredentialsBlock;
  persistedMagentoMetadata: Record<string, unknown> | undefined;
  persistedBqGa4Metadata: Record<string, unknown> | undefined;
  persistedKlaviyoMetadata: Record<string, unknown> | undefined;
  persistedKlaviyoOutboundMetadata: Record<string, unknown> | undefined;
  onToggleEdit: (next: boolean) => void;
  onChangeMagento: (next: MagentoCredentialsBlock) => void;
  onChangeBqGa4: (next: BqGa4CredentialsBlock) => void;
  onChangeKlaviyo: (next: KlaviyoCredentialsBlock) => void;
  onChangeKlaviyoOutbound: (next: KlaviyoOutboundCredentialsBlock) => void;
}) {
  return (
    <div className="form-section">
      <div className="form-section-head">
        <h3>Credentials</h3>
        {configured && (
          <label className="toggle-inline">
            <input
              type="checkbox"
              checked={editing}
              onChange={(e) => onToggleEdit(e.target.checked)}
            />
            <span>Update credentials</span>
          </label>
        )}
      </div>
      {configured && !editing && (
        <div className="readonly-note">
          Credentials are stored securely. Toggle "Update credentials" to rotate them. Updates
          are limited to once per 24 hours.
        </div>
      )}
      {(editing || !configured) && (
        <>
          {selected === "magento" ? (
            <MagentoFields
              value={magento}
              disabled={disabled}
              persistedMetadata={persistedMagentoMetadata}
              onChange={onChangeMagento}
            />
          ) : selected === "klaviyo" ? (
            <KlaviyoFields
              value={klaviyo}
              disabled={disabled}
              persistedMetadata={persistedKlaviyoMetadata}
              onChange={onChangeKlaviyo}
            />
          ) : selected === "klaviyo_outbound" ? (
            <KlaviyoOutboundFields
              value={klaviyoOutbound}
              disabled={disabled}
              persistedMetadata={persistedKlaviyoOutboundMetadata}
              onChange={onChangeKlaviyoOutbound}
            />
          ) : (
            <BqGa4Fields
              value={bqGa4}
              disabled={disabled}
              persistedMetadata={persistedBqGa4Metadata}
              onChange={onChangeBqGa4}
            />
          )}
        </>
      )}
    </div>
  );
}

function MagentoFields({
  value,
  disabled,
  persistedMetadata,
  onChange,
}: {
  value: MagentoCredentialsBlock;
  disabled: boolean;
  persistedMetadata: Record<string, unknown> | undefined;
  onChange: (next: MagentoCredentialsBlock) => void;
}) {
  const baseUrlHint = stringMeta(persistedMetadata?.base_url);
  const storeHint = stringMeta(persistedMetadata?.store_code) || "default";
  const websiteHint = stringMeta(persistedMetadata?.website_id);
  return (
    <div className="field-grid">
      <Field
        label="Base URL"
        value={value.base_url}
        disabled={disabled}
        placeholder={baseUrlHint || undefined}
        onChange={(base_url) => onChange({ ...value, base_url })}
      />
      <Field
        label="Store code"
        value={value.store_code}
        disabled={disabled}
        placeholder={storeHint}
        onChange={(store_code) => onChange({ ...value, store_code })}
      />
      <Field
        label="Website ID"
        value={value.website_id}
        disabled={disabled}
        placeholder={websiteHint || "1"}
        onChange={(website_id) => onChange({ ...value, website_id })}
      />
      <Field label="Consumer key" value={value.oauth_consumer_key} secret disabled={disabled} onChange={(oauth_consumer_key) => onChange({ ...value, oauth_consumer_key })} />
      <Field label="Consumer secret" value={value.oauth_consumer_secret} secret disabled={disabled} onChange={(oauth_consumer_secret) => onChange({ ...value, oauth_consumer_secret })} />
      <Field label="Access token" value={value.oauth_access_token} secret disabled={disabled} onChange={(oauth_access_token) => onChange({ ...value, oauth_access_token })} />
      <Field label="Access token secret" value={value.oauth_access_token_secret} secret disabled={disabled} onChange={(oauth_access_token_secret) => onChange({ ...value, oauth_access_token_secret })} />
    </div>
  );
}

function BqGa4Fields({
  value,
  disabled,
  persistedMetadata,
  onChange,
}: {
  value: BqGa4CredentialsBlock;
  disabled: boolean;
  persistedMetadata: Record<string, unknown> | undefined;
  onChange: (next: BqGa4CredentialsBlock) => void;
}) {
  const projectHint = stringMeta(persistedMetadata?.project_id);
  const datasetHint = stringMeta(persistedMetadata?.dataset_id);
  return (
    <div className="field-grid">
      <Field
        label="Project ID"
        value={value.project_id}
        disabled={disabled}
        placeholder={projectHint || undefined}
        onChange={(project_id) => onChange({ ...value, project_id })}
      />
      <Field
        label="Dataset ID"
        value={value.dataset_id}
        disabled={disabled}
        placeholder={datasetHint || undefined}
        onChange={(dataset_id) => onChange({ ...value, dataset_id })}
      />
      <label className="field wide">
        Service account JSON
        <textarea
          value={value.service_account_json}
          onChange={(e) => onChange({ ...value, service_account_json: e.target.value })}
          spellCheck={false}
          rows={10}
          disabled={disabled}
          placeholder={disabled ? "Stored securely" : undefined}
        />
      </label>
    </div>
  );
}

function KlaviyoFields({
  value,
  disabled,
  persistedMetadata,
  onChange,
}: {
  value: KlaviyoCredentialsBlock;
  disabled: boolean;
  persistedMetadata: Record<string, unknown> | undefined;
  onChange: (next: KlaviyoCredentialsBlock) => void;
}) {
  const revisionHint = stringMeta(persistedMetadata?.api_revision) || "2024-10-15";
  return (
    <div className="field-grid">
      <Field
        label="API key"
        value={value.api_key}
        secret
        disabled={disabled}
        placeholder={disabled ? "Stored securely" : "pk_live_..."}
        onChange={(api_key) => onChange({ ...value, api_key })}
      />
      <Field
        label="API revision (optional)"
        value={value.api_revision ?? ""}
        disabled={disabled}
        placeholder={revisionHint}
        onChange={(api_revision) => onChange({ ...value, api_revision })}
      />
    </div>
  );
}

function KlaviyoOutboundFields({
  value,
  disabled,
  persistedMetadata,
  onChange,
}: {
  value: KlaviyoOutboundCredentialsBlock;
  disabled: boolean;
  persistedMetadata: Record<string, unknown> | undefined;
  onChange: (next: KlaviyoOutboundCredentialsBlock) => void;
}) {
  const revisionHint = stringMeta(persistedMetadata?.api_revision) || "2024-10-15";
  const customerIdPropertyHint =
    stringMeta(persistedMetadata?.customer_id_property) || "magento_customer_id";
  return (
    <div className="field-grid">
      <Field
        label="API key (write-scoped)"
        value={value.api_key}
        secret
        disabled={disabled}
        placeholder={disabled ? "Stored securely" : "pk_live_..."}
        onChange={(api_key) => onChange({ ...value, api_key })}
      />
      <Field
        label="API revision (optional)"
        value={value.api_revision ?? ""}
        disabled={disabled}
        placeholder={revisionHint}
        onChange={(api_revision) => onChange({ ...value, api_revision })}
      />
      <Field
        label="Customer-ID property (optional)"
        value={value.customer_id_property ?? ""}
        disabled={disabled}
        placeholder={customerIdPropertyHint}
        onChange={(customer_id_property) => onChange({ ...value, customer_id_property })}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Expiry section
// ---------------------------------------------------------------------------

function ExpirySection({
  disabled,
  expiry,
  activationPending,
  persistedRecipients,
  onChange,
}: {
  disabled: boolean;
  expiry: ExpiryFormState;
  activationPending: boolean;
  persistedRecipients: string[];
  onChange: (next: ExpiryFormState) => void;
}) {
  const [draftEmail, setDraftEmail] = useState("");

  function addEmail() {
    const trimmed = draftEmail.trim();
    if (!trimmed) return;
    if (expiry.notificationEmails.includes(trimmed)) {
      setDraftEmail("");
      return;
    }
    if (expiry.notificationEmails.length >= MAX_NOTIFICATION_EMAILS) return;
    onChange({ ...expiry, notificationEmails: [...expiry.notificationEmails, trimmed] });
    setDraftEmail("");
  }

  function removeEmail(target: string) {
    onChange({
      ...expiry,
      notificationEmails: expiry.notificationEmails.filter((e) => e !== target),
    });
  }

  return (
    <div className="form-section">
      <div className="form-section-head">
        <h3>API key rotation</h3>
        <label className="toggle-inline">
          <input
            type="checkbox"
            checked={expiry.trackingEnabled}
            disabled={disabled}
            onChange={(e) => onChange({ ...expiry, trackingEnabled: e.target.checked })}
          />
          <span>Enable expiry tracking</span>
        </label>
      </div>
      {!expiry.trackingEnabled && (
        <div className="readonly-note">
          Optional. Turn this on to receive reminders before the API key expires.
        </div>
      )}
      {expiry.trackingEnabled && (
        <div className="field-grid">
          <label className="field">
            Expiry date (UTC)
            <input
              type="date"
              value={expiry.expiresAtUtc}
              disabled={disabled}
              onChange={(e) => onChange({ ...expiry, expiresAtUtc: e.target.value })}
            />
            {activationPending && !expiry.expiresAtUtc && (
              <small className="hint">
                Calculated from the activation date after Growdhi completes credential setup.
              </small>
            )}
          </label>
          <label className="field">
            Rotation cycle
            <select
              value={expiry.rotationCycle}
              disabled={disabled}
              onChange={(e) => onChange({ ...expiry, rotationCycle: e.target.value as RotationCycle })}
            >
              {ROTATION_CYCLE_OPTIONS.map((cycle) => (
                <option key={cycle} value={cycle}>
                  {formatRotationCycle(cycle)}
                </option>
              ))}
            </select>
          </label>
          {expiry.rotationCycle === "custom" && (
            <label className="field">
              Cycle (days)
              <input
                type="number"
                min={1}
                max={366}
                value={expiry.rotationCycleDays}
                disabled={disabled}
                onChange={(e) => onChange({ ...expiry, rotationCycleDays: e.target.value })}
              />
            </label>
          )}
          <label className="field wide">
            Notification emails ({expiry.notificationEmails.length}/{MAX_NOTIFICATION_EMAILS})
            <div className="email-chip-input">
              {expiry.notificationEmails.map((email) => (
                <span className="email-chip" key={email}>
                  {email}
                  <button
                    type="button"
                    className="email-chip-remove"
                    aria-label={`Remove ${email}`}
                    disabled={disabled}
                    onClick={() => removeEmail(email)}
                  >
                    x
                  </button>
                </span>
              ))}
              <input
                type="email"
                value={draftEmail}
                disabled={disabled || expiry.notificationEmails.length >= MAX_NOTIFICATION_EMAILS}
                placeholder={
                  expiry.notificationEmails.length === 0
                    ? "ops@yourcompany.com"
                    : "Add another"
                }
                onChange={(e) => setDraftEmail(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === ",") {
                    e.preventDefault();
                    addEmail();
                  }
                }}
                onBlur={addEmail}
              />
            </div>
            {persistedRecipients.length > 0 && expiry.notificationEmails.length === 0 && (
              <small className="hint">
                Currently saved: {persistedRecipients.join(", ")}
              </small>
            )}
          </label>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Required-access (scope) section
// ---------------------------------------------------------------------------

function ScopeRequirementsSection({
  requirements,
  statuses,
  checking,
}: {
  requirements: ScopeRequirement[];
  statuses: Record<string, ScopeStatus>;
  checking: boolean;
}) {
  if (requirements.length === 0) return null;
  const hasVerdict = Object.keys(statuses).length > 0;
  return (
    <div className="form-section">
      <div className="form-section-head">
        <h3>Required access</h3>
      </div>
      <div className="readonly-note">
        The credential must grant the access listed below. Grant it on the source,
        then validate.
        {checking
          ? " Checking each scope..."
          : hasVerdict
            ? " Status reflects the latest connection test."
            : ""}
      </div>
      <ul className="scope-list">
        {requirements.map((scope) => {
          // While a connection test is in flight, every row shows "Checking...";
          // the backend returns the per-scope verdict at the end of the probe.
          const status: ScopeStatus | "checking" | undefined = checking
            ? "checking"
            : statuses[scope.key];
          return (
            <li className="scope-row" key={scope.key}>
              <span className="scope-main">
                <strong>{scope.label}</strong>
                {scope.tier === "optional" && <em className="scope-optional"> (optional)</em>}
                <code className="scope-grant">{scope.resource_access}</code>
              </span>
              {status && (
                <span className={"status-pill scope-status-" + status}>
                  {scopeStatusLabel(status)}
                </span>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function scopeStatusLabel(status: ScopeStatus | "checking"): string {
  switch (status) {
    case "checking":
      return "Checking...";
    case "ok":
      return "Granted";
    case "denied":
      return "Denied";
    case "not_probed":
      return "Not checked";
    case "unavailable":
      return "Unavailable";
    case "unreachable":
      return "Unreachable";
    default:
      return status;
  }
}

function extractScopeStatuses(
  summary: Record<string, unknown> | undefined,
): Record<string, ScopeStatus> {
  return scopeStatusMap(summary?.scopes);
}

// Build a {scope key -> status} map from a scopes[] array, which appears both in
// a success validation_summary and in a failed test's error.details.scopes.
function scopeStatusMap(scopes: unknown): Record<string, ScopeStatus> {
  const out: Record<string, ScopeStatus> = {};
  if (!Array.isArray(scopes)) return out;
  for (const entry of scopes) {
    if (entry && typeof entry === "object") {
      const key = (entry as Record<string, unknown>).key;
      const status = (entry as Record<string, unknown>).status;
      if (typeof key === "string" && typeof status === "string") {
        out[key] = status as ScopeStatus;
      }
    }
  }
  return out;
}

// ---------------------------------------------------------------------------
// Shared field renderer
// ---------------------------------------------------------------------------

function Field({
  label,
  value,
  onChange,
  secret = false,
  disabled = false,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (next: string) => void;
  secret?: boolean;
  disabled?: boolean;
  placeholder?: string;
}) {
  return (
    <label className="field">
      {label}
      <input
        value={value}
        type={secret ? "password" : "text"}
        onChange={(e) => onChange(e.target.value)}
        spellCheck={false}
        disabled={disabled}
        placeholder={placeholder ?? (secret && disabled ? "Stored securely" : undefined)}
      />
    </label>
  );
}

// ---------------------------------------------------------------------------
// State helpers
// ---------------------------------------------------------------------------

/**
 * "Last updated" timestamp shown on each integration row.
 *
 * Sourced strictly from customer-driven writes:
 *   1. `credentials_updated_at_utc` - latest credential change via the UI.
 *   2. `submitted_at_utc` - first-time credentials submission.
 *
 * We intentionally do NOT fall back to `updated_at_utc`. That field is
 * also bumped by operator hygiene fixes (direct DDB writes outside the
 * Integrations API), which the customer never sees and shouldn't be
 * surfaced as Integrations-section activity.
 */
function lastCustomerActivity(state: IntegrationState): string | null {
  return state.credentials_updated_at_utc ?? state.submitted_at_utc ?? null;
}

function toFormState(expiry: ExpiryState): ExpiryFormState {
  if (!expiry.tracking_enabled) return EMPTY_EXPIRY;
  return {
    trackingEnabled: true,
    expiresAtUtc: expiry.expires_at_utc ?? "",
    rotationCycle: expiry.rotation_cycle ?? "quarterly",
    rotationCycleDays: expiry.rotation_cycle_days != null ? String(expiry.rotation_cycle_days) : "",
    notificationEmails: [...(expiry.notification_emails ?? [])],
  };
}

function expiryDirtyOrChanged(form: ExpiryFormState, server: ExpiryState | undefined): boolean {
  const serverState = server ?? null;
  const serverEnabled = !!serverState?.tracking_enabled;
  if (form.trackingEnabled !== serverEnabled) return true;
  if (!form.trackingEnabled) return false;
  if (form.expiresAtUtc !== (serverState?.expires_at_utc ?? "")) return true;
  if (form.rotationCycle !== (serverState?.rotation_cycle ?? "quarterly")) return true;
  const serverDays = serverState?.rotation_cycle_days != null
    ? String(serverState.rotation_cycle_days)
    : "";
  if (form.rotationCycleDays !== serverDays) return true;
  const serverEmails = serverState?.notification_emails ?? [];
  if (form.notificationEmails.length !== serverEmails.length) return true;
  return form.notificationEmails.some((e, i) => e !== serverEmails[i]);
}

function buildExpiryPolicyRequest(
  form: ExpiryFormState,
  expiryBasis: "explicit" | "cadence_derived" = "explicit",
): ExpiryPolicyRequest {
  if (!form.trackingEnabled) {
    return { tracking_enabled: false };
  }
  return {
    tracking_enabled: true,
    expiry_basis: expiryBasis,
    expires_at_utc: expiryBasis === "explicit" ? form.expiresAtUtc || undefined : undefined,
    rotation_cycle: form.rotationCycle,
    rotation_cycle_days: form.rotationCycle === "custom"
      ? Number(form.rotationCycleDays) || null
      : null,
    notification_emails: form.notificationEmails,
  };
}

function validateExpiryPolicy(policy: ExpiryPolicyRequest): string | null {
  if (!policy.tracking_enabled) return null;
  if (policy.expiry_basis !== "cadence_derived" && !policy.expires_at_utc) {
    return "Pick an expiry date.";
  }
  if (policy.expiry_basis !== "cadence_derived" && policy.expires_at_utc) {
    const today = new Date();
    today.setUTCHours(0, 0, 0, 0);
    const picked = new Date(policy.expires_at_utc + "T00:00:00.000Z");
    if (picked < today) return "Expiry date must be today or a future date.";
  }
  if (policy.rotation_cycle === "custom") {
    const days = policy.rotation_cycle_days;
    if (typeof days !== "number" || !Number.isFinite(days) || days < 1 || days > 366) {
      return "Custom cycle must be a positive number of days (1-366).";
    }
  }
  if (!policy.notification_emails || policy.notification_emails.length === 0) {
    return "Add at least one notification email.";
  }
  if (policy.notification_emails.length > MAX_NOTIFICATION_EMAILS) {
    return `Maximum ${MAX_NOTIFICATION_EMAILS} notification emails.`;
  }
  const emailRe = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
  for (const email of policy.notification_emails) {
    if (!emailRe.test(email)) return `Invalid email address: ${email}`;
  }
  return null;
}

interface CooldownInfo {
  active: boolean;
  label: string;
}

function computeCooldown(
  credentialsUpdatedAtUtc: string | undefined,
  submittedAtUtc: string | undefined,
  error: UiError | null,
): CooldownInfo {
  // Prefer the server's authoritative retry_after_seconds when we just got a 409.
  if (error?.cooldownSeconds !== undefined) {
    return { active: true, label: humanizeSeconds(error.cooldownSeconds) };
  }
  // Legacy rows predate `credentials_updated_at_utc`; fall back to
  // `submitted_at_utc` (the credential-submission time) so the UI warns
  // before submit instead of surfacing a surprise 409. Mirrors the backend
  // cooldown anchor in domain.source_connection.cooldown_active.
  const anchor = credentialsUpdatedAtUtc ?? submittedAtUtc;
  if (!anchor) return { active: false, label: "" };
  const last = Date.parse(anchor);
  if (Number.isNaN(last)) return { active: false, label: "" };
  const elapsed = (Date.now() - last) / 1000;
  const cooldown = 24 * 3600;
  if (elapsed >= cooldown) return { active: false, label: "" };
  return { active: true, label: humanizeSeconds(Math.ceil(cooldown - elapsed)) };
}

function humanizeSeconds(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return m ? `${h}h ${m}m` : `${h}h`;
}

function submitButtonLabel({
  editCredentials,
  checkingSource,
  cooldownActive,
}: {
  editCredentials: boolean;
  checkingSource: SourceSystem | null;
  cooldownActive: boolean;
}): string {
  if (checkingSource) return "Checking connection...";
  if (editCredentials && cooldownActive) return "Credentials locked (24h cooldown)";
  if (editCredentials) return "Validate and submit";
  return "Save expiry policy";
}

function pillTextForState(state: string, days: number | null | undefined): string {
  if (state === "rotation_pending") return "Activation pending";
  if (state === "expired") return "Expired";
  if (state.startsWith("notify_")) {
    if (days != null) {
      if (days === 1) return "Expires tomorrow";
      return `Expires in ${days}d`;
    }
    return state.replace(/^notify_/, "Expires in ").replace(/d$/, "d");
  }
  return state;
}

function labelStatus(status: string | undefined): string {
  if (status === "pending_operator_setup") return "Activation pending";
  if (status === "operator_completed") return "Configured successfully";
  if (status === "rejected") return "Needs attention";
  return "Not configured";
}

interface UiError {
  title: string;
  message: string;
  cooldownSeconds?: number;
}

function formatError(e: unknown): UiError {
  if (e instanceof ApiHttpError) {
    return friendlyApiError(e);
  }
  if (e instanceof Error) return { title: "Connection check failed", message: e.message };
  return { title: "Connection check failed", message: String(e) };
}

function friendlyApiError(err: ApiHttpError): UiError {
  if (err.code === "notification_send_in_progress") {
    return {
      title: "Expiry alert is finishing",
      message: err.message,
    };
  }
  if (err.code === "cooldown_active") {
    const retry = typeof err.details?.retry_after_seconds === "number"
      ? (err.details.retry_after_seconds as number)
      : undefined;
    return {
      title: "Credentials are locked",
      message: err.message,
      cooldownSeconds: retry,
    };
  }
  const message = cleanApiMessage(err.message);
  const lower = message.toLowerCase();
  if (err.status === 401 || err.status === 403) {
    return {
      title: "Check API settings",
      message: "The Tenant Admin API key or tenant access is not valid.",
    };
  }
  if (
    lower.includes("consumer having the specified key does not exist") ||
    lower.includes("http 401") ||
    lower.includes("http 403")
  ) {
    return {
      title: "Magento credentials were rejected",
      message: "Check the consumer key, access token, token secret, and backend read access.",
    };
  }
  // True missing-field errors are raised before probing and carry no per-scope
  // detail. A scope/role failure ("missing required BigQuery access") DOES carry
  // error.details.scopes - don't misclassify it as a missing-field error.
  if (lower.includes("missing required") && !Array.isArray(err.details?.scopes)) {
    return {
      title: "Complete required fields",
      message: "Fill in every required field before validating the connection.",
    };
  }
  if (lower.includes("service_account_json must be valid json")) {
    return {
      title: "Service account JSON is invalid",
      message: "Paste the full Google service-account JSON file and try again.",
    };
  }
  if (lower.includes("dataset not found") || lower.includes("bigquery")) {
    return {
      title: "BigQuery access failed",
      message,
    };
  }
  if (err.status >= 500) {
    return {
      title: "Connection check failed",
      message: "The API hit an internal error while validating this connection.",
    };
  }
  return { title: "Connection check failed", message };
}

function cleanApiMessage(message: string): string {
  return message
    .replace(/^Magento validation failed:\s*/i, "")
    .replace(/^Klaviyo validation failed:\s*/i, "")
    .replace(/^GA4 BigQuery validation failed:\s*/i, "");
}

function isConfigured(status: string | undefined): boolean {
  return status === "pending_operator_setup" || status === "operator_completed";
}

function submissionNotice(source: SourceSystem, status: string | undefined): string {
  if (status === "pending_operator_setup") {
    return `${sourceDisplayName(source)} credentials validated. Growdhi activation is pending.`;
  }
  return `${sourceDisplayName(source)} configured successfully.`;
}

function sourceDisplayName(source: SourceSystem): string {
  if (source === "magento") return "Magento";
  if (source === "klaviyo") return "Klaviyo";
  if (source === "klaviyo_outbound") return "Klaviyo Outbound";
  return "GA4 BigQuery";
}

function stringMeta(value: unknown): string {
  return typeof value === "string" ? value : "";
}

function persistedCheckKey(config: ApiConfig): string {
  return `growdhi.tenantAdminApi.integrationCheck.${config.tenantId}.${config.baseUrl}`;
}

function persistChecking(
  config: ApiConfig,
  source: SourceSystem,
  baselineGenerationId: string | null,
): void {
  localStorage.setItem(
    persistedCheckKey(config),
    JSON.stringify({
      source,
      baselineGenerationId,
      expiresAt: Date.now() + CHECK_TTL_MS,
    }),
  );
}

function readPersistedCheck(config: ApiConfig): PersistedIntegrationCheck | null {
  try {
    const raw = localStorage.getItem(persistedCheckKey(config));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PersistedIntegrationCheck>;
    if (!parsed.source || !parsed.expiresAt || parsed.expiresAt < Date.now()) {
      clearPersistedCheck(config);
      return null;
    }
    return {
      source: parsed.source,
      baselineGenerationId: parsed.baselineGenerationId ?? null,
      expiresAt: parsed.expiresAt,
    };
  } catch {
    clearPersistedCheck(config);
    return null;
  }
}

function clearPersistedCheck(config: ApiConfig): void {
  localStorage.removeItem(persistedCheckKey(config));
}
